import { Controller, FormProvider, useForm } from "react-hook-form";
import { useMemo, useState } from "react";
import { type DropdownEntry } from "~/common/types";
import { openModal } from "~/AddToCart/ModalSevice";

import Modal from "~/common/components/Modal";
import { Trans, useTranslation, type WithTranslation } from "react-i18next";
import { Button } from "~/common/components/Button/Button";
import { MaterialInput } from "~/common/components/materialInput/MaterialInput";
import { SimpleSelect } from "~/common/components/Select/SimpleSelect";
import Parameter from "~/common/components/Parameter";
import { IconType } from "~/common/components/icon/IconType";
import Checkbox from "~/common/components/Checkbox";
import {
  ADDRESS_MAX_LENGTH,
  CITY_MAX_LENGTH,
  COMPANY_NAME_MAX_LENGTH,
  HOUSENUM_MAX_LENGTH,
  STREET_MAX_LENGTH,
  ZIPCODE_MAX_LENGTH,
  ZIPCODE_MIN_LENGTH,
  ZIPCODE_REGEXP,
} from "~/utils/validations";
import { makeSelectRef4RHF } from "~/MyAccount/formUtils";
import { LinkButton } from "~/common/components/Link";
import { Icon } from "~/common/components/icon/Icon";
import { splitAddressLine1 } from "~/utils/addressUtils";
import { Stacker } from "~/common/components/Stacker/Stacker";
import type { AddressData } from "../../Address";

const IS_HOUSE_FIRST = window.SCHA?.config?.isHouseNumFirst ?? false;

interface Props {
  onClose: () => void;
  onSave: (address: AddressForm & { id?: string }) => void;
  onDelete: (address: AddressData) => void;
  address: AddressData | null;
  countriesList: {
    isocode: string;
    name: string;
  }[];
}

interface AddressForm {
  companyName: string;
  street: string;
  houseNumber: string;
  addressAdditional: string;
  zipCode: string;
  city: string;
  country: string;
  persistForLater: boolean;
}

function makeInitialFormData(address: AddressData | null, countryOptions: DropdownEntry<string>[]): AddressForm {
  const { houseNumber, street } = splitAddressLine1(address?.lines[0] ?? "");

  let country = address?.country.isocode ?? "";
  if (!country && countryOptions.length === 1) {
    country = countryOptions[0].value;
  }

  return {
    companyName: address?.companyName ?? "",
    zipCode: address?.postalCode ?? "",
    addressAdditional: address?.lines[1] ?? "",
    city: address?.town ?? "",
    country,
    street,
    houseNumber,
    persistForLater: !address?.temporary,
  };
}

function processCountries(countriesList: Props["countriesList"], t: WithTranslation["t"]): DropdownEntry<string>[] {
  return countriesList.map((country) => ({
    label: t(country.name),
    value: country.isocode,
  }));
}

export const CreateEditAddressModal = (props: Props) => {
  const { t } = useTranslation();
  const countryOptions = useMemo(() => processCountries(props.countriesList, t), [props.countriesList]);

  const { onClose, onSave, onDelete } = props;
  const [initialData] = useState(() => makeInitialFormData(props.address, countryOptions));
  const id = props.address?.id;

  const isEditMode = !!id;

  const formHooks = useForm<AddressForm>({ defaultValues: initialData });

  const onDeleteCb = async () => {
    if (await requestConfirmation()) {
      onDelete(props.address!);
      onClose();
    }
  };

  const submitCb = formHooks.handleSubmit((data) => {
    onSave({ ...data, id });
  });

  const makeErrorMessage = (suffix: string) => (fieldCode: string) =>
    t(`checkoutPage.addressModal.input.error.${suffix}`, {
      fieldname: t(`checkoutPage.addressModal.input.label.${fieldCode}`).toLowerCase(),
    });

  const makeTooLongMessage = (fieldCode: string) =>
    t(`checkoutPage.addressModal.error.isTooLongPart`, {
      fieldname: t(`checkoutPage.addressModal.input.label.${fieldCode}`),
    });
  const makePleaseEnterMessage = (fieldCode: string) =>
    t(`checkoutPage.addressModal.error.pleaseEnter`, {
      fieldname: t(`checkoutPage.addressModal.input.label.${fieldCode}`).toLowerCase(),
    });

  return (
    <Modal
      onClose={onClose}
      title={t(`checkoutPage.addressModal.${isEditMode ? "edit" : "add"}.title`)}
      footerChildren={
        <div className="d-flex flex-justify-between flex-align-center w-100">
          <p>{"*" + t("checkoutPage.addressModal.mandatory")}</p>
          <Stacker direction={"row"} gap={8}>
            <Button variant={"text"} data-test-id={"address-modal-btn-cancel"} onClick={onClose}>
              {t("checkoutPage.addressModal.cancel")}
            </Button>
            <Button data-test-id={"address-modal-btn-save"} onClick={submitCb}>
              {t("checkoutPage.addressModal.save")}
            </Button>
          </Stacker>
        </div>
      }
    >
      <div className="pt-1">
        <FormProvider {...formHooks}>
          <Controller
            name={"companyName"}
            rules={{
              required: makePleaseEnterMessage("companyName"),
              maxLength: {
                value: COMPANY_NAME_MAX_LENGTH,
                message: makeTooLongMessage("companyName"),
              },
            }}
            render={({ field: { ref, ...rest }, fieldState: { error } }) => (
              <MaterialInput
                data-test-id={"address-modal-input-company"}
                label={t("checkoutPage.addressModal.input.label.companyName") + "*"}
                inputRef={ref}
                {...rest}
                helperLabel={error?.message}
                error={!!error}
              />
            )}
          />

          <div className="scha__edit-address-modal__form-row">
            <Controller
              name={"houseNumber"}
              rules={{
                required: makePleaseEnterMessage("houseNumber"),
                maxLength: {
                  value: HOUSENUM_MAX_LENGTH,
                  message: makeTooLongMessage("houseNumber"),
                },
              }}
              render={({ field: { ref, ...rest }, fieldState: { error } }) => (
                <MaterialInput
                  style={{ order: IS_HOUSE_FIRST ? -1 : 1 }}
                  data-test-id={"address-modal-input-address-house-num"}
                  label={t("checkoutPage.addressModal.input.label.houseNumber") + "*"}
                  inputRef={ref}
                  {...rest}
                  helperLabel={error?.message}
                  error={!!error}
                />
              )}
            />

            <Controller
              name={"street"}
              rules={{
                required: makePleaseEnterMessage("street"),
                maxLength: {
                  value: STREET_MAX_LENGTH,
                  message: makeTooLongMessage("street"),
                },
              }}
              render={({ field: { ref, ...rest }, fieldState: { error } }) => (
                <MaterialInput
                  data-test-id={"address-modal-input-address-street"}
                  label={t("checkoutPage.addressModal.input.label.street") + "*"}
                  inputRef={ref}
                  {...rest}
                  helperLabel={error?.message}
                  error={!!error}
                />
              )}
            />
          </div>

          <div className="scha__edit-address-modal__form-row">
            <Controller
              name={"addressAdditional"}
              rules={{
                maxLength: {
                  value: ADDRESS_MAX_LENGTH,
                  message: makeTooLongMessage("addressAdditional"),
                },
              }}
              render={({ field: { ref, ...rest }, fieldState: { error } }) => (
                <MaterialInput
                  data-test-id={"address-modal-input-address-additional"}
                  label={t("checkoutPage.addressModal.input.label.addressAdditional")}
                  inputRef={ref}
                  {...rest}
                  helperLabel={error?.message}
                  error={!!error}
                />
              )}
            />
          </div>

          <div className="scha__edit-address-modal__form-row">
            <Controller
              name={"zipCode"}
              rules={{
                required: makePleaseEnterMessage("zipCode"),
                minLength: {
                  value: ZIPCODE_MIN_LENGTH,
                  message: makeErrorMessage("tooShortPart")("zipCode"),
                },
                maxLength: {
                  value: ZIPCODE_MAX_LENGTH,
                  message: makeTooLongMessage("zipCode"),
                },
                pattern: {
                  value: ZIPCODE_REGEXP,
                  message: makeErrorMessage("forbiddenCharactersPart")("zipCode"),
                },
              }}
              render={({ field: { ref, ...rest }, fieldState: { error } }) => (
                <MaterialInput
                  data-test-id={"address-modal-input-zip"}
                  label={t("checkoutPage.addressModal.input.label.zipCode") + "*"}
                  inputRef={ref}
                  {...rest}
                  helperLabel={error?.message}
                  error={!!error}
                />
              )}
            />

            <Controller
              name={"city"}
              rules={{
                required: makePleaseEnterMessage("city"),
                maxLength: {
                  value: CITY_MAX_LENGTH,
                  message: makeTooLongMessage("city"),
                },
              }}
              render={({ field: { ref, ...rest }, fieldState: { error } }) => (
                <MaterialInput
                  data-test-id={"address-modal-input-city"}
                  label={t("checkoutPage.addressModal.input.label.city") + "*"}
                  inputRef={ref}
                  {...rest}
                  helperLabel={error?.message}
                  error={!!error}
                />
              )}
            />
          </div>
          <div className="scha__edit-address-modal__form-row">
            {countryOptions.length > 1 && (
              <Controller
                name={"country"}
                rules={{
                  required: makePleaseEnterMessage("country"),
                }}
                render={({ field: { onChange, ref, ...rest }, fieldState: { error } }) => (
                  <SimpleSelect
                    label={t("checkoutPage.addressModal.input.label.country") + "*"}
                    data-test-id={"address-modal-dropdown-country"}
                    values={countryOptions}
                    error={!!error}
                    helperText={error?.message}
                    onChange={(e) => onChange(e.target.value)}
                    ref={makeSelectRef4RHF(ref)}
                    {...rest}
                  />
                )}
              />
            )}
            {countryOptions.length === 1 && (
              <Parameter label={t("checkoutPage.addressModal.input.label.country")}>
                <span className="title-medium">{countryOptions[0].label}</span>
              </Parameter>
            )}
          </div>

          <div className={"mt-3 mb-30 d-flex flex-align-center"}>
            {isEditMode && (
              <LinkButton
                data-test-id={"address-modal-btn-delete"}
                onClick={onDeleteCb}
                iconBefore={<Icon type={IconType.Bin} />}
                className="pt-3 pb-30"
              >
                {t("checkoutPage.addressModal.delete")}
              </LinkButton>
            )}
            {!isEditMode && (
              <>
                <Controller
                  name={"persistForLater"}
                  render={({ field: { value, onChange } }) => (
                    <>
                      <Checkbox data-test-id={"address-modal-cbox-save-for-later"} checked={value} onCheck={onChange} />
                      <span className="ml-2" style={{ paddingTop: "3px" }}>
                        {t("checkoutPage.addressModal.saveForLater")}
                      </span>
                    </>
                  )}
                />
              </>
            )}
          </div>
        </FormProvider>
      </div>
    </Modal>
  );
};

function requestConfirmation() {
  return new Promise((resolve) => {
    openModal("Address-delete-confirmation", (close) => {
      const onClose = () => {
        close();
        resolve(false);
      };
      const onConfirm = () => {
        close();
        resolve(true);
      };
      return (
        <Modal
          onClose={onClose}
          footerChildren={
            <Stacker direction="row" gap={8}>
              <Button variant={"text"} data-test-id={"address-modal-btn-no"} onClick={onClose}>
                <Trans i18nKey={"checkoutPage.addressModal.no"} />
              </Button>
              <Button data-test-id={"address-modal-btn-no"} className="ml-30" onClick={onConfirm}>
                <Trans i18nKey={"checkoutPage.addressModal.yes"} />
              </Button>
            </Stacker>
          }
        >
          <div className="pt-30 pb-30">
            <Trans i18nKey={"checkoutPage.addressModal.info.areYouSure"} />
          </div>
        </Modal>
      );
    });
  });
}
